import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { use100vh } from 'react-div-100vh';
import { usePrevious, useEventListener } from '@24i/player-utils-react';
import { useEventsManager } from '@24i/player-ui';
import { PlayerContainer, VideoContainer, PlayerUI } from './styled';
export const Player = ({ engine, source, autoPlay, startPosition, startPositionType, quality, abrStrategy, onError, onAdBreakStart, onAdBreakEnd, onEnd, onBitrateChange, onPlay, onPause, onSeeking, onSeekingOutOfDVR, onSeeked, children, setVideoPlayerRef, isCheckingBlockers, hasBlockingReason = false, fullscreenContainerOverride = null, }) => {
    const playerContainer = useRef(null);
    const fullscreenContainer = useRef(null);
    const [videoPlayer, setVideoPlayer] = useState(null);
    const prevSource = usePrevious(source);
    const prevVideoPlayer = usePrevious(videoPlayer || undefined);
    const prevEngine = usePrevious(engine);
    const destroyRef = useRef();
    const setVideoPlayerRefRef = useRef(setVideoPlayerRef);
    setVideoPlayerRefRef.current = setVideoPlayerRef;
    const [isEmbeddedAdUiShown, setIsEmbeddedAdUiShown] = useState(false);
    const viewportHeight = use100vh();
    useEventsManager({
        onPlay,
        onPause,
        onSeeking,
        onSeekingOutOfDVR,
        onSeeked,
        onEnd,
        onAdBreakStart,
        onAdBreakEnd,
        onBitrateChange,
    }, videoPlayer);
    const onVideoContainerClick = useCallback(() => {
        if (isEmbeddedAdUiShown && (videoPlayer === null || videoPlayer === void 0 ? void 0 : videoPlayer.paused) && videoPlayer.currentTime === 0) {
            videoPlayer.play();
        }
    }, [videoPlayer, isEmbeddedAdUiShown]);
    const onErrorHandler = useCallback((event) => {
        if (onError)
            onError(event.error);
    }, [onError]);
    const addEventListeners = (playerInstance) => {
        playerInstance.addEventListener('error', onErrorHandler);
    };
    const removeEventListeners = (playerInstance) => {
        playerInstance.removeEventListener('error', onErrorHandler);
    };
    const destroy = useCallback(() => {
        if (!videoPlayer) {
            return;
        }
        videoPlayer.destroy();
        setVideoPlayer(null);
        if (setVideoPlayerRefRef.current) {
            setVideoPlayerRefRef.current(null);
        }
    }, [videoPlayer]);
    destroyRef.current = destroy;
    useEffect(() => {
        return () => { var _a; return (_a = destroyRef.current) === null || _a === void 0 ? void 0 : _a.call(destroyRef); };
    }, []);
    useEffect(() => {
        if (!engine ||
            !playerContainer.current ||
            !fullscreenContainer.current ||
            isCheckingBlockers)
            return;
        if (engine === prevEngine) {
            if (videoPlayer) {
                const didUrlChange = (source === null || source === void 0 ? void 0 : source.url) !== (prevSource === null || prevSource === void 0 ? void 0 : prevSource.url);
                if (source !== prevSource) {
                    try {
                        videoPlayer.source = source;
                    }
                    catch (error) {
                        onError(error);
                        destroy();
                    }
                    if (didUrlChange) {
                        videoPlayer.load().catch((error) => {
                            onError(error);
                            destroy();
                        });
                    }
                }
                videoPlayer.autoPlay = autoPlay;
                videoPlayer.startPosition = startPosition;
                videoPlayer.startPositionType = startPositionType;
                videoPlayer.abrStrategy = abrStrategy;
                videoPlayer.quality = quality;
                return;
            }
        }
        destroy();
        const newVideoPlayer = engine.createPlayer({
            source,
            autoPlay,
            startPosition,
            startPositionType,
            abrStrategy,
            quality,
        }, playerContainer.current, (fullscreenContainerOverride === null || fullscreenContainerOverride === void 0 ? void 0 : fullscreenContainerOverride.current) || fullscreenContainer.current);
        if (prevVideoPlayer && prevVideoPlayer !== newVideoPlayer) {
            removeEventListeners(prevVideoPlayer);
        }
        addEventListeners(newVideoPlayer);
        setVideoPlayer(newVideoPlayer);
        if (setVideoPlayerRefRef.current)
            setVideoPlayerRefRef.current(newVideoPlayer);
        newVideoPlayer
            .initializePlayer()
            .then(() => {
        })
            .catch(() => {
            newVideoPlayer.destroy();
            setVideoPlayer(null);
            if (setVideoPlayerRefRef.current) {
                setVideoPlayerRefRef.current(null);
            }
        });
    }, [engine, source, prevSource, isCheckingBlockers]);
    useEventListener('adstarted', useCallback((e) => {
        setIsEmbeddedAdUiShown(e.embeddedUi === true);
    }, []), videoPlayer);
    useEventListener('adbreakended', useCallback(() => setIsEmbeddedAdUiShown(false), []), videoPlayer);
    const fullscreenStyle = useMemo(() => ({
        maxHeight: viewportHeight,
    }), [viewportHeight]);
    return (React.createElement(PlayerContainer, { ref: fullscreenContainer, style: fullscreenStyle },
        React.createElement(VideoContainer, { testID: "playing_content", "data-test": "player-video-container", onClick: onVideoContainerClick, ref: playerContainer }),
        React.createElement(PlayerUI, { "data-test": "player-ui-container", isEmbeddedAdUiShown }, (videoPlayer || hasBlockingReason) &&
            typeof children === 'function' &&
            children(videoPlayer))));
};
//# sourceMappingURL=index.js.map